import React, { ReactElement, ReactNode, useState } from 'react'
import { Flex, Text } from 'theme-ui'
import { Svg, Button } from 'ui'
import WarningIcon from 'images/featuresPages/warning-icon.svg'
import { Setter } from 'utils'
import UnionIcon from 'images/featuresPages/union.svg'

interface TechnicalLimitationsSectionProps {
  expandedContent: ReactNode
}

export default function TechnicalLimitationsSection({
  expandedContent,
}: TechnicalLimitationsSectionProps): ReactElement {
  const [isExpanded, setIsExpanded] = useState(false)

  return (
    <Flex
      data-testid="technical-limitations"
      sx={{
        position: 'relative',
        maxWidth: '70.3rem',
        px: '2.4rem',
        pt: '2.4rem',
        pb: '4.8rem',
        backgroundColor: 'banana10',
        alignItems: 'center',
        borderRadius: '12px',
        color: 'banana70',
        flexDirection: ['column', 'row'],
      }}
    >
      <Svg
        svg={WarningIcon}
        descriptiveText="Technical limitations warning"
        sx={{
          width: '4.8rem',
          height: '4.8rem',
          flexShrink: 0,
          mr: [0, '2.4rem'],
          mb: ['2.4rem', 0],
          alignSelf: ['center', 'flex-start'],
        }}
      />
      <Flex
        sx={{
          flexDirection: 'column',
          gap: '1.6rem',
          textAlign: 'left',
        }}
      >
        <Text
          sx={{
            maxWidth: '27rem',
            fontWeight: 700,
            fontSize: '2.1rem',
            color: 'banana',
          }}
        >
          Technical Limitations for Advanced Users
        </Text>
        <Flex
          sx={{
            flexDirection: 'column',
            gap: '1.6rem',
            height: isExpanded ? ['33rem', '25.2rem'] : ['13.5rem', '9.5rem'],
            textAlign: 'left',
            transition: '0.3s height linear',
            overflow: 'hidden',
          }}
        >
          {isExpanded ? (
            expandedContent
          ) : (
            <Text
              sx={{
                fontSize: ['1.8rem', '2.1rem'],
              }}
            >
              The information contained here is applicable to a very small subset of users - if you
              don&apos;t understand the terminology, it&apos;s unlikely that this will apply to you.
            </Text>
          )}
        </Flex>
      </Flex>
      <PlusButton isLimitationsExpanded={isExpanded} setIsLimitationsExpanded={setIsExpanded} />
    </Flex>
  )
}

const PlusButton = ({
  isLimitationsExpanded,
  setIsLimitationsExpanded,
}: {
  isLimitationsExpanded: boolean
  setIsLimitationsExpanded: Setter<boolean>
}): ReactElement => {
  return (
    <Button
      variant="simple"
      ariaLabel="expand or collapse technical limitations button"
      data-testid="expand-collapse-technical-limitations-button"
      sx={{
        position: 'absolute',
        left: '50%',
        bottom: '0',
        width: '4.8rem',
        height: '4.8rem',
        // we can't use a <100% opacity color for this button as its background is 2 colors
        backgroundColor: 'rgb(40 38 30)',
        transform: 'translate(-50%, 50%)',
        justifyContent: 'center',
        alignItems: 'center',
        borderRadius: '50%',
        border: '3px solid',
        borderColor: 'darkBodyBG',
        cursor: 'pointer',
      }}
      onClick={() => {
        setIsLimitationsExpanded(!isLimitationsExpanded)
      }}
    >
      <Svg
        svg={UnionIcon}
        descriptiveText={
          isLimitationsExpanded ? 'Collapse technical limitations' : 'Expand technical limitations'
        }
        sx={{
          transform: isLimitationsExpanded ? 'rotate(45deg)' : 'rotate(0)',
          transition: '0.3s transform',
        }}
      />
    </Button>
  )
}
